import AppContext from 'App/AppContext';
import { IPIcon, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { CircleRingIcon } from 'components/Checklist/CheckListStyles';
import { canUpdateTaskField, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import {
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalTextBig,
  globalTextFieldBig,
} from 'globalStyles';
import Task from 'models/tasks/task';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from 'utils/string';
import { iconFontSize, readOnlyRowSpace } from './TaskDetailStyles';

interface ITaskDetailNameProps {
  orgTask: Task;
  task: Task;
  canUpdate: boolean;
  readonly?: boolean;
  compactView?: boolean;
  onUpdate: (newTask: Task) => void;
  getErrorMessage: (field: string) => string | undefined;
  //An error resolved outside the synchronous validation - a uniqueness check that had to ask the
  //server. Fluent only re-runs onGetErrorMessage on a value change, so a verdict that lands later
  //has to arrive through errorMessage or it is never painted.
  errorMessage?: string;
}

export const TaskDetailName = (props: ITaskDetailNameProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task', 'translation']);
  const isReadonly =
    props.readonly || !canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Name, appContext);

  const getPlaceholder = (): string => {
    if (props.task.isWorkableTask()) {
      return t('task:Title.Placeholder');
    } else if (props.task.isEvent()) {
      return t('task:Title.PlaceholderEvent');
    } else if (props.task.isTemplate() || props.task.isSeries()) {
      return t('task:Title.PlaceholderTemplate');
    } else {
      //default
      return t('task:Title.Placeholder');
    }
  };

  //
  // Main render
  //
  if (props.compactView && isReadonly) {
    //hide name because in compact mode it is in the title of the modal
    return null;
  }

  return (
    <IPStack
      horizontal
      tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
      verticalAlign="start"
      styles={isReadonly ? { root: { paddingBottom: readOnlyRowSpace / 2 } } : undefined}
    >
      <IPStack.Item styles={{ root: { marginTop: iconFontSize.marginTop } }}>
        {!appContext.isMobileView && <IPIcon {...CircleRingIcon} />}
      </IPStack.Item>
      <IPStack.Item grow>
        {isReadonly && <IPText styles={globalTextBig}>{capitalizeFirstLetter(props.task.name)}</IPText>}
        {!isReadonly && (
          <IPTextField
            className="isoplanner-task-name"
            underlined
            autoFocus={!appContext.isMobileView}
            value={props.task.name ? capitalizeFirstLetter(props.task.name) : ''}
            styles={globalTextFieldBig}
            errorMessage={props.errorMessage}
            onGetErrorMessage={(value) => {
              if (value === '') return '';

              return props.getErrorMessage('name');
            }}
            placeholder={getPlaceholder()}
            onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
              const copyTask = props.task.clone();
              copyTask.name = newValue ? newValue.trimStart() : '';
              props.onUpdate(copyTask);
            }}
            maxLength={512}
          />
        )}
      </IPStack.Item>
    </IPStack>
  );
};
